import { useState, useEffect, useCallback } from 'react';
import { Card, IconButton } from '../ui';
import { ArrowUturnLeftIcon, ArrowPathIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { useDatabases } from '../../hooks/useDatabases';

export default function JobHistory({ company, onJobReverted, refreshKey }) {
  const { data: databases = [] } = useDatabases();
  const dbLookup = Object.fromEntries(databases.map(db => [db.id || db.slug, db.name]));
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [reverting, setReverting] = useState(null);
  const [unreverting, setUnreverting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [error, setError] = useState(null);

  const loadJobs = useCallback(async () => {
    if (!company) return;

    try {
      setLoading(true);
      setError(null);
      console.log('[JobHistory] Fetching jobs for company:', company.id);
      const response = await api.get(`/cms/companies/${company.id}/jobs`);
      console.log('[JobHistory] Raw response:', response);
      console.log('[JobHistory] Response data:', response.data);
      console.log('[JobHistory] Response data type:', typeof response.data);
      console.log('[JobHistory] Is array:', Array.isArray(response.data));
      console.log('[JobHistory] Length:', response.data?.length);
      console.log('[JobHistory] Object keys:', Object.keys(response.data || {}));
      console.log('[JobHistory] Stringified:', JSON.stringify(response.data).substring(0, 200));
      // Ensure response.data is an array
      const jobsData = Array.isArray(response.data) ? response.data : [];
      console.log('[JobHistory] Setting jobs:', jobsData);
      setJobs(jobsData);
    } catch (err) {
      console.error('[JobHistory] Error loading jobs:', err);
      console.error('[JobHistory] Error response:', err.response);
      setError('Failed to load job history');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [company]);

  useEffect(() => {
    console.log('[JobHistory] useEffect triggered - company:', company?.id, 'refreshKey:', refreshKey);
    if (company) {
      loadJobs();
    } else {
      setJobs([]);
    }
  }, [company, loadJobs, refreshKey]);

  const handleRevert = async (job) => {
    if (!window.confirm(`Are you sure you want to revert this upload? This will deactivate ${job.rowsValid} responses.`)) {
      return;
    }

    try {
      setReverting(job.id);
      setError(null);

      await api.post(`/cms/jobs/${job.id}/revert`);

      // Reload jobs
      await loadJobs();

      // Notify parent
      if (onJobReverted) {
        onJobReverted();
      }
    } catch (err) {
      console.error('Error reverting job:', err);
      setError(err.response?.data?.error || 'Failed to revert job');
    } finally {
      setReverting(null);
    }
  };

  const handleUnrevert = async (job) => {
    if (!window.confirm(`Are you sure you want to restore this upload? This will reactivate ${job.rowsValid} responses.`)) {
      return;
    }

    try {
      setUnreverting(job.id);
      setError(null);

      await api.post(`/cms/jobs/${job.id}/unrevert`);

      // Reload jobs
      await loadJobs();

      // Notify parent
      if (onJobReverted) {
        onJobReverted();
      }
    } catch (err) {
      console.error('Error unrevesting job:', err);
      setError(err.response?.data?.error || 'Failed to restore job');
    } finally {
      setUnreverting(null);
    }
  };

  const handleDelete = async (job) => {
    if (!window.confirm(`Are you sure you want to permanently delete this job? This cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(job.id);
      setError(null);

      await api.delete(`/cms/jobs/${job.id}`);

      // Reload jobs
      await loadJobs();

      // Notify parent
      if (onJobReverted) {
        onJobReverted();
      }
    } catch (err) {
      console.error('Error deleting job:', err);
      setError(err.response?.data?.error || 'Failed to delete job');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);
    return date.toLocaleString();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✓';
      case 'reverted':
        return '⚠';
      case 'failed':
        return '✗';
      case 'processing':
        return '⏳';
      default:
        return '•';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 dark:text-green-400';
      case 'reverted':
        return 'text-yellow-600 dark:text-yellow-400';
      case 'failed':
        return 'text-red-600 dark:text-red-400';
      case 'processing':
        return 'text-blue-600 dark:text-blue-400';
      default:
        return 'text-text-muted-light dark:text-text-muted';
    }
  };

  if (!company) {
    return (
      <Card>
        <h3 className="text-lg font-semibold text-text-secondary-light dark:text-text-secondary mb-2">
          Upload History
        </h3>
        <p className="text-sm text-text-muted-light dark:text-text-muted">
          Select a company to view upload history
        </p>
      </Card>
    );
  }

  console.log('[JobHistory] Rendering - loading:', loading, 'jobs.length:', jobs.length, 'error:', error);

  return (
    <Card>
      <h3 className="text-lg font-semibold text-text-secondary-light dark:text-text-secondary mb-3">
        Upload History
      </h3>

      {error && (
        <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm text-red-600 dark:text-red-400">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-4 text-text-muted-light dark:text-text-muted text-sm">
          Loading jobs...
        </div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-4 text-text-muted-light dark:text-text-muted text-sm">
          No uploads yet (checked {jobs.length} jobs)
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar">
          {jobs.map(job => (
            <div
              key={job.id}
              className="border border-border dark:border-border rounded-lg p-3 text-sm"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`font-bold ${getStatusColor(job.status)}`}>
                    {getStatusIcon(job.status)}
                  </span>
                  <span className="font-medium text-text-primary-light dark:text-text-primary capitalize">
                    {job.status}
                  </span>
                  {job.jobType === 'survey' && (
                    <span className="text-xs px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 font-medium">
                      Survey
                    </span>
                  )}
                  {job.databaseId && (
                    <span className="text-[11px] px-1.5 py-0 rounded font-medium bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">
                      {dbLookup[job.databaseId] || job.databaseId}
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {job.status === 'completed' && (
                    <IconButton
                      icon={ArrowUturnLeftIcon}
                      label={reverting === job.id ? 'Reverting...' : 'Revert Upload'}
                      variant="warning"
                      size="sm"
                      display="icon"
                      onClick={() => handleRevert(job)}
                      disabled={reverting === job.id}
                    />
                  )}
                  {job.status === 'reverted' && (
                    <>
                      <IconButton
                        icon={ArrowPathIcon}
                        label={unreverting === job.id ? 'Restoring...' : 'Restore Upload'}
                        variant="primary"
                        size="sm"
                        display="icon"
                        onClick={() => handleUnrevert(job)}
                        disabled={unreverting === job.id}
                      />
                      <IconButton
                        icon={TrashIcon}
                        label={deleting === job.id ? 'Deleting...' : 'Delete Upload'}
                        variant="danger"
                        size="sm"
                        display="icon"
                        onClick={() => handleDelete(job)}
                        disabled={deleting === job.id}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="space-y-1 text-text-secondary-light dark:text-text-secondary">
                <div>
                  {job.jobType === 'survey' ? (
                    <><span className="text-text-muted-light dark:text-text-muted">Survey:</span> {job.surveyTitle}</>
                  ) : (
                    <><span className="text-text-muted-light dark:text-text-muted">File:</span> {job.filename}</>
                  )}
                </div>
                <div>
                  <span className="text-text-muted-light dark:text-text-muted">Rows:</span>{' '}
                  {job.rowsValid} valid
                  {job.rowsInvalid > 0 && `, ${job.rowsInvalid} invalid`}
                </div>
                <div>
                  <span className="text-text-muted-light dark:text-text-muted">Uploaded:</span>{' '}
                  {formatDate(job.uploadedAt)}
                </div>
                {job.dateRange && job.dateRange.earliest && job.dateRange.latest && (
                  <div>
                    <span className="text-text-muted-light dark:text-text-muted">Date Range:</span>{' '}
                    {formatDate(job.dateRange.earliest)} - {formatDate(job.dateRange.latest)}
                  </div>
                )}
                <div>
                  <span className="text-text-muted-light dark:text-text-muted">By:</span> {job.uploadedBy}
                </div>

                {job.status === 'reverted' && job.revertedAt && (
                  <div className="text-yellow-600 dark:text-yellow-400">
                    <span className="text-text-muted-light dark:text-text-muted">Reverted:</span>{' '}
                    {formatDate(job.revertedAt)} by {job.revertedBy}
                  </div>
                )}

                {job.errorLog && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-red-600 dark:text-red-400 hover:opacity-70 transition">
                      View errors ({job.rowsInvalid} rows)
                    </summary>
                    <pre className="mt-2 p-2 bg-gray-50 dark:bg-background-gray rounded text-xs overflow-x-auto">
                      {JSON.stringify(JSON.parse(job.errorLog), null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
